import { ChangeEvent, useCallback, useEffect, useId, useState } from 'react';

export interface FloatingInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  patternMismatchText?: string;
}

// 1. Accepts an initial value so external data can populate the input
export const usePhoneFormat = (initialValue: string = '') => {
  const [displayPhone, setDisplayPhone] = useState<string>('');
  const [firebasePhone, setFirebasePhone] = useState<string>('');

  // 2. Safely sync external value changes (e.g., data fetched from an API)
  useEffect(() => {
    if (initialValue) {
      const cleaned = initialValue.replace(/\D/g, '');
      const limited = cleaned.length === 11 ? cleaned.slice(1, 10) : cleaned;
      if (limited.length === 10) {
        setDisplayPhone(
          `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`,
        );
        setFirebasePhone(`+1${limited}`);
      } else {
        setDisplayPhone(limited);
        setFirebasePhone(limited);
      }
    }
  }, [initialValue]);

  const handlePhoneChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): string => {
      const input = e.target.value;
      const cleaned = input.replace(/\D/g, '');
      const limited = cleaned.slice(0, 10);

      const fbPhone = limited.length === 10 ? `+1${limited}` : limited;
      setFirebasePhone(fbPhone);

      if (limited.length === 10) {
        setDisplayPhone(
          `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`,
        );
      } else {
        setDisplayPhone(limited);
      }

      return fbPhone;
    },
    [],
  );

  return { displayPhone, firebasePhone, handlePhoneChange };
};

export const FloatingInput: React.FC<FloatingInputProps> = ({
  label,
  id,
  type = 'text',
  value,
  defaultValue,
  ...props
}) => {
  const uniqueId = useId();
  const inputId = id || uniqueId;

  // Only initialize the specific string value to prevent object-object passing
  const stringValue = (value || defaultValue || '') as string;
  const { displayPhone, firebasePhone, handlePhoneChange } =
    usePhoneFormat(stringValue);

  // --- NEW: Track if the user has interacted with the field ---
  const [isTouched, setIsTouched] = useState(false);

  // Validation is only "unlocked" if they left the field OR clicked submit
  const showValidation = isTouched;

  return (
    <div className="relative w-full mb-[30px]">
      <input
        id={inputId}
        placeholder=" "
        type={type === 'tel' ? 'tel' : type} // Ensure standard text types pass through safely
        // Safely trigger setIsTouched when the user clicks out of the input
        onBlur={(e) => {
          setIsTouched(true);
          if (props.onBlur) props.onBlur(e); // Preserve any external onBlur handlers
        }}
        className="peer w-full h-[50px] bg-brand-cream rounded-[10px] border border-grey-20 px-5 text-black outline-none transition-all focus:border-2 focus:border-brand-frost hover:border-brand-truffle focus:ring-0 disabled:bg-gray-50 disabled:cursor-not-allowed"
        {...props}
        {...(type === 'tel'
          ? {
              onChange: (e: ChangeEvent<HTMLInputElement>) => {
                const fbNum = handlePhoneChange(e);
                if (props.onChange) {
                  // Safely clone the event to preserve target.name and other properties
                  const clonedEvent = {
                    ...e,
                    target: { ...e.target, value: fbNum, name: props.name },
                  } as ChangeEvent<HTMLInputElement>;
                  props.onChange(clonedEvent);
                }
              },
              value: displayPhone,
              autoComplete: 'tel-national',
              // Pattern is now applied to the VISIBLE input, formatted to match what the user sees
              pattern:
                props.pattern ||
                '^\\([2-9]\\d{2}\\)\\s\\d{3}-\\d{4}$|^\\d{10}$',
            }
          : {
              value: value,
              defaultValue: defaultValue,
            })}
      />

      {/* Native hidden inputs CANNOT be validated. 
          Use type="hidden", remove 'peer', and remove required/pattern. */}
      {type === 'tel' && (
        <input type="hidden" name={props.name} value={firebasePhone} />
      )}

      <label
        htmlFor={inputId}
        className="absolute left-4 top-0 tracking-widest -translate-y-1/2 bg-brand-cream px-1 text-[12px] font-[500] text-text-muted transition-all duration-200 pointer-events-none
          peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-[16px] peer-placeholder-shown:font-normal peer-placeholder-shown:text-black/60
          peer-focus:top-0 peer-focus:text-[12px] peer-focus:font-[500] peer-focus:text-text-muted"
      >
        {label}
      </label>

      {type === 'tel' && (
        <span className="absolute opacity-100 right-0 mr-3 top-0 flex h-full items-center justify-center peer-placeholder-shown:opacity-0 transition-opacity font-[500] tracking-widest text-gray-700 text-[12px]">
          US
        </span>
      )}

      {/* --- UPDATED: Error Styling with the showValidation Gatekeeper --- */}

      {/* 1. The Red Line Indicator */}
      <div
        className={`after:content-[''] after:absolute after:top-[10px] after:left-2 after:h-[30px] after:w-0 after:invisible after:border-r-3 after:border-brand-error after:rounded-full after:duration-300 after:transition-discrete ${
          showValidation ? 'peer-[:not(:focus):invalid]:after:visible' : ''
        }`}
      />

      {/* 2. The Error Message & Icon */}
      <div
        className={`absolute h-[25px] top-[50px] left-[1rem] flex gap-2 items-center duration-300 transition-discrete ${
          showValidation
            ? 'invisible peer-[:not(:focus):invalid]:visible'
            : 'invisible'
        }`}
      >
        <svg
          width="12px"
          height="12px"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="12" cy="12" r="10" fill="var(--color-brand-error)" />
          <text
            x="50%"
            y="52%"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="white"
            fontFamily="sans-serif"
            fontSize="14px"
            fontWeight="bold"
          >
            !
          </text>
        </svg>
        <span className="text-[10px]">This field is required</span>
      </div>
    </div>
  );
};
